/**
 * The unsent ZorgMail message a user is writing, kept in the browser so a reload or an accidental
 * close does not throw it away. There are no server-side drafts.
 *
 * `sessionStorage`, keyed per user, and forgotten at sign-out: a draft is PHI, so it may outlive
 * neither the tab nor the session it was written in, and a colleague signing in on the same
 * workstation never sees it. One draft per user — the compose dialog is one at a time — tagged
 * with what it answers, so a draft written as a reply to one message is never offered on another.
 */
const KEY_PREFIX = 'scrivio_zorgmail_compose_draft:'

export interface ZorgMailComposeDraft {
  to: string
  cc: string
  subject: string
  body: string
  forwardedAttachmentIndexes: number[]
  documentClient: { id: string; name: string } | null
  documents: { id: string; name: string; size: number }[]
}

interface StoredDraft {
  context: string
  draft: ZorgMailComposeDraft
}

export function saveZorgMailComposeDraft(userId: string, context: string, draft: ZorgMailComposeDraft): void {
  try {
    sessionStorage.setItem(KEY_PREFIX + userId, JSON.stringify({ context, draft } satisfies StoredDraft))
  } catch {
    // Storage refused: the draft lives as long as the dialog, which is what it had before
  }
}

/** The stored draft when it was written for [context], else null. */
export function loadZorgMailComposeDraft(userId: string, context: string): ZorgMailComposeDraft | null {
  try {
    const raw = sessionStorage.getItem(KEY_PREFIX + userId)
    if (!raw) return null
    const stored = JSON.parse(raw) as StoredDraft
    return stored.context === context ? stored.draft : null
  } catch {
    return null
  }
}

export function discardZorgMailComposeDraft(userId: string): void {
  try {
    sessionStorage.removeItem(KEY_PREFIX + userId)
  } catch {
    // Nothing stored that could be discarded
  }
}

/** Forget every user's draft. Called when the user signs out. */
export function forgetZorgMailComposeDrafts(): void {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(KEY_PREFIX))
      .forEach((key) => sessionStorage.removeItem(key))
  } catch {
    // Nothing stored that could be forgotten
  }
}
